<template>
  <v-navigation-drawer
    v-model="localValue"
    fixed
    right
    temporary
    width="600"
    class="execution-details-panel"
  >
    <v-toolbar flat>
      <v-toolbar-title class="text-h6">
        {{ $t('job-manager.job-details') }}
      </v-toolbar-title>
      <v-spacer />
      <v-btn icon @click="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <v-card-text v-if="execution" class="pa-4">
      <!-- Execution Overview -->
      <v-card class="mb-4" elevation="1">
        <v-card-title class="pb-2">
          <span class="text-subtitle-1">{{ $t('job-manager.details') }}</span>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">{{ $t('job-manager.id') }}:</span>
                <span class="detail-value">{{ execution.id }}</span>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">{{ $t('job-manager.status') }}:</span>
                <v-chip
                  small
                  :color="getStatusColor(execution.status)"
                  text-color="white"
                >
                  {{ execution.status }}
                </v-chip>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">{{ $t('job-manager.definition') }}:</span>
                <span class="detail-value">{{ execution.definition }}</span>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">{{ $t('job-manager.queue') }}:</span>
                <span class="detail-value">{{ execution.queue }}</span>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">{{ $t('job-manager.duration') }}:</span>
                <span class="detail-value">{{ execution.duration }}</span>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">{{ $t('job-manager.attempt') }}:</span>
                <span class="detail-value">{{ execution.attempt }}</span>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Timeline -->
      <v-card class="mb-4" elevation="1">
        <v-card-title class="pb-2">
          <span class="text-subtitle-1">{{ $t('job-manager.execution-timeline') }}</span>
        </v-card-title>
        <v-card-text>
          <v-timeline dense>
            <v-timeline-item
              v-for="(step, index) in executionSteps"
              :key="index"
              :color="getStatusColor(step.status)"
              small
            >
              <template v-slot:opposite>
                <span class="text-caption text--secondary">
                  {{ step.timestamp }}
                </span>
              </template>
              <v-card class="elevation-2">
                <v-card-text class="pa-3">
                  <div class="d-flex align-center mb-1">
                    <span class="font-weight-medium">{{ step.name }}</span>
                    <v-spacer />
                    <v-chip
                      x-small
                      :color="getStatusColor(step.status)"
                      text-color="white"
                    >
                      {{ step.status }}
                    </v-chip>
                  </div>
                  <div v-if="step.duration" class="text-caption text--secondary">
                    {{ $t('job-manager.duration') }}: {{ step.duration }}
                  </div>
                  <div v-if="step.message" class="text-body-2 mt-1">
                    {{ step.message }}
                  </div>
                </v-card-text>
              </v-card>
            </v-timeline-item>
          </v-timeline>
        </v-card-text>
      </v-card>

      <!-- Error Details (if failed) -->
      <v-card v-if="execution.status === 'FAILED' && execution.errorMessage" class="mb-4" elevation="1">
        <v-card-title class="pb-2 error--text">
          <v-icon left color="error">mdi-alert-circle</v-icon>
          <span class="text-subtitle-1">{{ $t('job-manager.error-details') }}</span>
        </v-card-title>
        <v-card-text>
          <pre class="error-message">{{ execution.errorMessage }}</pre>
        </v-card-text>
      </v-card>

      <!-- Actions -->
      <v-card elevation="1">
        <v-card-title class="pb-2">
          <span class="text-subtitle-1">{{ $t('job-manager.actions') }}</span>
        </v-card-title>
        <v-card-actions>
          <v-btn
            v-if="canRetry"
            color="primary"
            outlined
            @click="retryExecution"
          >
            <v-icon left>mdi-refresh</v-icon>
            {{ $t('job-manager.retry') }}
          </v-btn>
          <v-btn
            v-if="canCancel"
            color="error"
            outlined
            @click="cancelExecution"
          >
            <v-icon left>mdi-stop</v-icon>
            {{ $t('job-manager.cancel') }}
          </v-btn>
          <v-spacer />
          <v-btn
            color="grey"
            text
            @click="close"
          >
            {{ $t('job-manager.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-card-text>

    <!-- Empty State -->
    <v-card-text v-else class="text-center pa-8">
      <v-icon large color="grey lighten-1">mdi-information-outline</v-icon>
      <p class="text-body-1 text--secondary mt-4 mb-0">
        {{ $t('job-manager.no-execution-selected') }}
      </p>
    </v-card-text>
  </v-navigation-drawer>
</template>

<script>
export default {
  name: 'ExecutionDetailsPanel',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    execution: {
      type: Object,
      default: null
    }
  },
  computed: {
    localValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    executionSteps() {
      if (!this.execution) return [];

      // Mock timeline steps based on execution data
      const steps = [
        {
          name: this.$t('job-manager.task-created'),
          status: 'SUCCESS',
          timestamp: this.execution.timestamp || new Date().toLocaleString(),
          duration: null,
          message: this.$t('job-manager.task-created-message', { definition: this.execution.definition, queue: this.execution.queue })
        }
      ];

      if (this.execution.status === 'RUNNING') {
        steps.push({
          name: this.$t('job-manager.task-running'),
          status: 'RUNNING',
          timestamp: new Date().toLocaleString(),
          duration: this.execution.duration,
          message: this.$t('job-manager.task-processing')
        });
      } else if (this.execution.status === 'SUCCESS') {
        steps.push(
          {
            name: this.$t('job-manager.task-started'),
            status: 'SUCCESS',
            timestamp: new Date(Date.now() - 5000).toLocaleString(),
            duration: '1s',
            message: this.$t('job-manager.task-started-success')
          },
          {
            name: this.$t('job-manager.task-completed'),
            status: 'SUCCESS',
            timestamp: new Date().toLocaleString(),
            duration: this.execution.duration,
            message: this.$t('job-manager.task-completed-success')
          }
        );
      } else if (this.execution.status === 'FAILED') {
        steps.push(
          {
            name: 'Démarrage',
            status: 'SUCCESS',
            timestamp: new Date(Date.now() - 5000).toLocaleString(),
            duration: '1s',
            message: 'Tâche démarrée'
          },
          {
            name: 'Échec',
            status: 'FAILED',
            timestamp: new Date().toLocaleString(),
            duration: this.execution.duration,
            message: 'Erreur lors de l\'exécution de la tâche'
          }
        );
      }

      return steps;
    },
    canRetry() {
      return this.execution && ['FAILED', 'CANCELLED'].includes(this.execution.status);
    },
    canCancel() {
      return this.execution && ['RUNNING', 'PENDING'].includes(this.execution.status);
    }
  },
  methods: {
    close() {
      this.localValue = false;
    },
    getStatusColor(status) {
      const colors = {
        'SUCCESS': 'green',
        'FAILED': 'red',
        'RUNNING': 'blue',
        'PENDING': 'orange',
        'CANCELLED': 'grey'
      };
      return colors[status] || 'grey';
    },
    retryExecution() {
      this.$emit('retry', this.execution);
      this.close();
    },
    cancelExecution() {
      this.$emit('cancel', this.execution);
      this.close();
    }
  }
};
</script>

<style lang="scss" scoped>
.execution-details-panel {
  ::v-deep .v-navigation-drawer__content {
    overflow-y: auto;
  }
}

.detail-item {
  margin-bottom: 8px;

  .detail-label {
    font-weight: 500;
    color: #666;
    margin-right: 8px;
  }

  .detail-value {
    color: #333;
  }
}

.error-message {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  padding: 12px;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  color: #dc2626;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 200px;
  overflow-y: auto;
}

::v-deep .v-timeline {
  .v-timeline-item__body {
    .v-card {
      .v-card__text {
        font-size: 0.875rem;
      }
    }
  }
}
</style>